/**
 * "My location": a live dot with accuracy circle and heading cone.
 *   off → (waiting) → follow: the map keeps the position in view
 *       → compass (phones with a compass): the map turns with the device
 *       → off.
 * Dragging the map leaves follow mode ("free": the dot stays, the button re-centres).
 * The watch pauses while the page is hidden.
 */
import { icon, toast, fmt, reducedMotion, setPressed } from './ui.js';
import { HANOI_BBOX } from './config.js';
import { bboxContains } from './core/geo.js';
import { act } from './telemetry.js';

let pm = null, btn = null, onFix = null, padding = () => ({});
let mode = 'off';             // off | wait | follow | compass | free
let watchId = null;
let fix = null;               // latest { lat, lng, acc, heading, speed }
let shown = null;             // animated dot position
let marker = null, el = null;
let heading = null;           // smoothed device heading (degrees from north)
let compassSeen = false;      // a heading reading arrived (Android: no permission needed)
let compassDenied = false;
let orientType = null;
let first = true;
let raf = 0;
let lastCam = 0;
let lastTurn = 0;
let waitGuard = 0;

const IOS_PERM = typeof DeviceOrientationEvent !== 'undefined' && typeof DeviceOrientationEvent.requestPermission === 'function';
const HAS_ORIENT = typeof window !== 'undefined' && ('ondeviceorientationabsolute' in window || 'ondeviceorientation' in window);
const coarse = () => matchMedia('(pointer: coarse)').matches;
const compassPossible = () => !compassDenied && HAS_ORIENT && coarse() && (IOS_PERM || compassSeen);

export const gpsMode = () => mode;
export const gpsFix = () => fix;

export function initGps({ planMap, button, onFirstFix, getPadding }) {
  pm = planMap;
  btn = button;
  onFix = onFirstFix;
  padding = getPadding || padding;
  btn.addEventListener('click', onButton);
  const map = pm.map;
  // user gestures leave follow / compass mode (programmatic camera moves carry no originalEvent)
  map.on('dragstart', () => { if (mode === 'follow' || mode === 'compass') setMode('free'); });
  map.on('rotatestart', (e) => { if (e.originalEvent && mode === 'compass') setMode('free'); });
  document.addEventListener('visibilitychange', () => {
    if (mode === 'off') return;
    if (document.hidden) { stopWatch(); stopOrient(); }
    else { startWatch(); if (mode !== 'wait') startOrient(); }
  });
  sync();
}

/* ---------------- modes ---------------- */

async function onButton() {
  switch (mode) {
    case 'off': start(); break;
    case 'wait': stop(); break;
    case 'free': setMode('follow'); camera(true); break;
    case 'follow':
      if (compassPossible()) {
        if (IOS_PERM) {
          let r = 'denied';
          try { r = await DeviceOrientationEvent.requestPermission(); } catch { /* not allowed */ }
          if (r !== 'granted') { compassDenied = true; toast('Chưa được phép dùng la bàn của thiết bị', { type: 'info' }); sync(); return; }
          startOrient();
        }
        setMode('compass');
        act('gps', 'compass');
        if (heading != null) turn(true);
        else toast('Xoay nhẹ điện thoại để la bàn định hướng', { type: 'info', ms: 2400 });
      } else stop();
      break;
    case 'compass': stop(); break;
    default:
  }
}

function start() {
  if (!navigator.geolocation) { toast('Trình duyệt không hỗ trợ định vị', { type: 'err' }); return; }
  first = true;
  setMode('wait');
  act('gps', 'start');
  startWatch();
  // the permission prompt can stay unanswered (or hidden): say so instead of spinning silently
  clearTimeout(waitGuard);
  waitGuard = setTimeout(() => { if (mode === 'wait') toast('Đang chờ vị trí… Hãy cho phép trình duyệt truy cập vị trí.', { type: 'warn', ms: 4200 }); }, 12000);
}

function stop() {
  const wasCompass = mode === 'compass';
  setMode('off');
  stopWatch();
  stopOrient();
  clearTimeout(waitGuard);
  cancelAnimationFrame(raf);
  marker?.remove();
  marker = el = null;
  fix = shown = null;
  heading = null;
  pm.setData('gps', null);
  if (wasCompass && pm.map) pm.map.easeTo({ bearing: 0, duration: reducedMotion() ? 0 : 500 });
}

function setMode(m) {
  mode = m;
  sync();
}

function sync() {
  if (!btn) return;
  const tips = {
    off: 'Vị trí của tôi',
    wait: 'Đang xác định vị trí… (nhấn để huỷ)',
    follow: compassPossible() ? 'Bật la bàn: bản đồ xoay theo hướng nhìn' : 'Tắt định vị',
    compass: 'Tắt định vị',
    free: 'Về vị trí của tôi',
  };
  const ic = { off: 'locate', wait: 'loader-circle', follow: 'locate-fixed', compass: 'navigation-2', free: 'locate' }[mode];
  btn.innerHTML = icon(ic, mode === 'wait' ? 'spin' : '');
  btn.dataset.gps = mode;
  btn.setAttribute('data-tip', tips[mode]);
  btn.setAttribute('aria-label', tips[mode]);
  setPressed(btn, mode === 'follow' || mode === 'compass');
  btn.classList.toggle('busy', mode === 'wait');
}

/* ---------------- position ---------------- */

function startWatch() {
  if (watchId != null || !navigator.geolocation) return;
  watchId = navigator.geolocation.watchPosition(onPosition, onError, { enableHighAccuracy: true, maximumAge: 4000, timeout: 25000 });
}

function stopWatch() {
  if (watchId != null) navigator.geolocation.clearWatch(watchId);
  watchId = null;
}

function onPosition(p) {
  const { latitude: lat, longitude: lng, accuracy, heading: h, speed } = p.coords;
  if (mode === 'off') return;
  if (first && !bboxContains(HANOI_BBOX, lng, lat)) {
    toast('Vị trí hiện tại của bạn nằm ngoài phạm vi dữ liệu Hà Nội', { type: 'info', ms: 3800 });
    stop();
    return;
  }
  fix = { lat, lng, acc: accuracy || 0, heading: Number.isFinite(h) && speed > 1.2 ? h : null, speed: speed || 0 };
  drawDot();
  if (first) {
    first = false;
    clearTimeout(waitGuard);
    setMode('follow');
    startOrient();
    onFix?.({ lat, lng, accuracy });
    toast(`Đã xác định vị trí · sai số khoảng ${fmt.dist(accuracy)}`, { type: 'ok', ms: 2600 });
    camera(true);
  } else if (mode === 'follow' || mode === 'compass') camera(false);
}

function onError(err) {
  if (mode === 'off') return;
  if (err.code === 1) {
    toast('Bạn chưa cho phép truy cập vị trí. Hãy bật quyền vị trí cho trang này trong trình duyệt.', { type: 'err', ms: 5200 });
    stop();
  } else if (mode === 'wait' && err.code === 2) {
    toast('Không xác định được vị trí, hãy thử lại ở nơi thoáng hơn', { type: 'err' });
    stop();
  }
  // timeouts: the watch keeps going
}

/** Keep the dot in view (first fix: zoom in). */
function camera(force) {
  const map = pm.map;
  if (!map || !fix) return;
  const now = performance.now();
  if (!force && now - lastCam < 900) return;
  lastCam = now;
  const opts = { center: [fix.lng, fix.lat], padding: padding(), essential: true };
  if (force) opts.zoom = Math.max(map.getZoom(), fix.acc > 250 ? 15.5 : 17.2);
  if (mode === 'compass' && heading != null) opts.bearing = heading;
  if (reducedMotion()) map.jumpTo(opts);
  else if (force) map.flyTo({ ...opts, speed: 1.6, maxDuration: 1800 });
  else map.easeTo({ ...opts, duration: 700 });
}

/* ---------------- dot ---------------- */

function ensureMarker() {
  if (marker) return;
  el = document.createElement('button');
  el.type = 'button';
  el.className = 'gps-dot';
  el.setAttribute('aria-label', 'Vị trí của bạn: xem quy hoạch tại đây');
  el.innerHTML = '<span class="gps-cone"></span><span class="gps-pulse"></span><span class="gps-core"></span>';
  el.addEventListener('click', (e) => { e.stopPropagation(); if (fix) onFix?.({ lat: fix.lat, lng: fix.lng, accuracy: fix.acc, again: true }); });
  marker = new window.maplibregl.Marker({ element: el, rotationAlignment: 'map', pitchAlignment: 'map' }).setLngLat([fix.lng, fix.lat]).addTo(pm.map);
  shown = { lat: fix.lat, lng: fix.lng };
}

function drawDot() {
  if (!fix) return;
  const fresh = !marker;
  ensureMarker();
  el.classList.toggle('coarse', fix.acc > 120);
  orient();
  const to = { lat: fix.lat, lng: fix.lng };
  const from = shown || to;
  const far = Math.hypot((to.lat - from.lat) * 110540, (to.lng - from.lng) * 111320 * Math.cos(to.lat * Math.PI / 180)) > 250;
  cancelAnimationFrame(raf);
  if (fresh || far || reducedMotion()) { shown = to; marker.setLngLat([to.lng, to.lat]); circle(); return; }
  const t0 = performance.now(), dur = 650;
  const step = (t) => {
    const k = Math.min(1, (t - t0) / dur), e = 1 - Math.pow(1 - k, 3);
    shown = { lat: from.lat + (to.lat - from.lat) * e, lng: from.lng + (to.lng - from.lng) * e };
    marker?.setLngLat([shown.lng, shown.lat]);
    if (k < 1) raf = requestAnimationFrame(step); else circle();
  };
  raf = requestAnimationFrame(step);
}

/** Accuracy circle (64-gon in metres). */
function circle() {
  if (!fix) return;
  const r = Math.min(fix.acc, 3000);
  if (r < 4) { pm.setData('gps', null); return; }
  const kx = 111320 * Math.cos(fix.lat * Math.PI / 180), ky = 110540;
  const ring = [];
  for (let i = 0; i <= 64; i++) { const a = (i / 64) * Math.PI * 2; ring.push([fix.lng + (r * Math.cos(a)) / kx, fix.lat + (r * Math.sin(a)) / ky]); }
  pm.setData('gps', { type: 'FeatureCollection', features: [{ type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [ring] } }] });
}

/** Heading cone: device compass, else the direction of travel. */
function orient() {
  if (!marker) return;
  const h = heading ?? fix?.heading;
  el.classList.toggle('has-heading', h != null);
  if (h != null) marker.setRotation(h);
}

/* ---------------- compass ---------------- */

function startOrient() {
  if (orientType || !HAS_ORIENT || !coarse()) return;
  if (IOS_PERM && mode !== 'compass') return; // iOS asks for permission: only when the compass is wanted
  orientType = 'ondeviceorientationabsolute' in window ? 'deviceorientationabsolute' : 'deviceorientation';
  addEventListener(orientType, onOrient);
}

function stopOrient() {
  if (orientType) removeEventListener(orientType, onOrient);
  orientType = null;
}

function onOrient(e) {
  let h = null;
  if (typeof e.webkitCompassHeading === 'number' && e.webkitCompassHeading >= 0) h = e.webkitCompassHeading;
  else if (e.absolute && typeof e.alpha === 'number') h = 360 - e.alpha;
  if (h == null || !Number.isFinite(h)) return;
  const ang = screen.orientation?.angle ?? (typeof window.orientation === 'number' ? window.orientation : 0);
  h = (((h + ang) % 360) + 360) % 360;
  heading = heading == null ? h : smooth(heading, h, 0.22);
  if (!compassSeen) { compassSeen = true; sync(); }
  orient();
  if (mode === 'compass') turn(false);
}

const smooth = (a, b, k) => { const d = ((b - a + 540) % 360) - 180; return (a + d * k + 360) % 360; };

function turn(force) {
  const map = pm.map;
  const now = performance.now();
  if (!map || heading == null || (!force && now - lastTurn < 120)) return;
  const d = Math.abs(((heading - map.getBearing() + 540) % 360) - 180);
  if (!force && d < 2) return;
  lastTurn = now;
  map.easeTo({ bearing: heading, center: fix ? [fix.lng, fix.lat] : undefined, padding: padding(), duration: force ? 400 : 160, easing: (t) => t, essential: true });
}
